import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/components/Toaster';
import { trackMetaEvent } from '@/lib/meta-pixel';

type Mode = 'sign_in' | 'sign_up' | 'reset';

// Blocklist client-side — espelha o banco (public.blocked_email_domains).
// Evita round-trip quando o domínio é óbvio. Validação final acontece no trigger.
const DISPOSABLE_DOMAINS = new Set([
  'tempmail.com','temp-mail.org','temp-mail.io','guerrillamail.com','guerrillamail.net',
  'guerrillamail.org','guerrillamailblock.com','sharklasers.com','mailinator.com',
  'mailinator.net','mailnesia.com','10minutemail.com','10minutemail.net','20minutemail.com',
  'yopmail.com','yopmail.net','yopmail.fr','trashmail.com','trashmail.net','dispostable.com',
  'throwawaymail.com','getnada.com','nada.email','getairmail.com','maildrop.cc',
  'fakemail.net','fake.com','example.com','example.org','example.net','test.com',
  'mohmal.com','mohmal.in','emailondeck.com','harakirimail.com','inboxbear.com',
  'mytemp.email','burnermail.io','spamgourmet.com','discard.email','mint.email',
  'incognitomail.com','cuvox.de','einrot.com','mvrht.com','armyspy.com','fleckens.hu',
  'teleworm.us','dayrep.com','rhyta.com','jourrapide.com','gustr.com','superrito.com',
  'mailcatch.com','mailnull.com','meltmail.com','mailforspam.com','mailsac.com',
  'spambox.us','spam4.me','trbvm.com','noclickemail.com','tmpmail.net','tmpmail.org',
  'emailfake.com','tempmailo.com','tempail.com','moakt.com','emailsensei.com',
  'tempinbox.com','vomoto.com','linshiyou.com','lilyhoneys.com','mailbox.in.ua',
  'mailpoof.com','emailtemporanea.com','binkmail.com','emltmp.com',
]);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const parseMode = (raw: string | null): Mode => {
  if (raw === 'sign_up' || raw === 'signup' || raw === 'register') return 'sign_up';
  if (raw === 'reset' || raw === 'recover') return 'reset';
  return 'sign_in';
};

export const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = React.useState<Mode>(() => parseMode(searchParams.get('mode')));
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();
    if (!EMAIL_RE.test(normalizedEmail)) {
      toast({
        title: 'Email inválido',
        description: 'Confira o formato do endereço.',
        variant: 'destructive',
      });
      return;
    }

    if (mode === 'sign_up') {
      const domain = normalizedEmail.split('@')[1] ?? '';
      if (DISPOSABLE_DOMAINS.has(domain)) {
        toast({
          title: 'Email descartável não permitido',
          description: 'Use um email profissional (domínio próprio, Gmail, Outlook, etc.) para receber seus 3 créditos grátis.',
          variant: 'destructive',
        });
        return;
      }
      if (password.length < 8) {
        toast({
          title: 'Senha muito curta',
          description: 'Use no mínimo 8 caracteres.',
          variant: 'destructive',
        });
        return;
      }
    }

    setSubmitting(true);
    try {
      if (mode === 'sign_in') {
        const { error } = await supabase.auth.signInWithPassword({ email: normalizedEmail, password });
        if (error) throw error;
        navigate('/dashboard', { replace: true });
      } else if (mode === 'sign_up') {
        trackMetaEvent('Lead', { content_name: 'portico_signup', content_category: 'portico' });
        const { error, data } = await supabase.auth.signUp({
          email: normalizedEmail,
          password,
        });
        if (error) {
          const msg = (error as Error).message ?? '';
          if (msg.toLowerCase().includes('email_domain_blocked') || msg.toLowerCase().includes('database error')) {
            throw new Error('Esse domínio de email não é aceito. Use um email profissional.');
          }
          throw error;
        }

        const fireSignupSuccess = (userId?: string): void => {
          const params: Record<string, unknown> = { content_name: 'portico_signup', content_category: 'portico' };
          if (userId) params.eventID = userId;
          trackMetaEvent('CompleteRegistration', params);
        };

        // Fire-and-forget: welcome email via Brevo. Não bloqueia o dashboard.
        const fireWelcomeEmail = (): void => {
          const supaUrl = import.meta.env.VITE_SUPABASE_URL as string;
          const supaAnon = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
          if (!supaUrl || !supaAnon) return;
          const firstName = normalizedEmail.split('@')[0]?.replace(/[^\p{L}\p{N}\s]/gu, '').split(/[\s.]+/)[0] ?? '';
          void fetch(`${supaUrl}/functions/v1/welcome-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${supaAnon}` },
            body: JSON.stringify({ email: normalizedEmail, first_name: firstName }),
          }).catch(() => {
            // silenciado — falha de welcome email não pode atrapalhar onboarding
          });
        };

        if (data.session) {
          fireSignupSuccess(data.session.user?.id);
          fireWelcomeEmail();
          toast({
            title: 'Conta criada · 3 créditos grátis',
            description: 'Vamos direto pro dashboard.',
            variant: 'success',
          });
          navigate('/dashboard', { replace: true });
        } else {
          const { data: signInData, error: signInErr } = await supabase.auth.signInWithPassword({ email: normalizedEmail, password });
          if (signInErr) throw signInErr;
          fireSignupSuccess(signInData.session?.user?.id);
          fireWelcomeEmail();
          toast({
            title: 'Conta criada · 3 créditos grátis',
            description: 'Vamos direto pro dashboard.',
            variant: 'success',
          });
          navigate('/dashboard', { replace: true });
        }
      } else if (mode === 'reset') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth/callback`,
        });
        if (error) throw error;
        toast({
          title: 'Link enviado',
          description: 'Verifique seu email para redefinir a senha.',
          variant: 'success',
        });
        setMode('sign_in');
      }
    } catch (err) {
      toast({
        title: mode === 'sign_in' ? 'Não conseguimos entrar' : mode === 'sign_up' ? 'Não conseguimos criar conta' : 'Não enviou o link',
        description: (err as Error).message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const titles: Record<Mode, { title: string; desc: string; button: string }> = {
    sign_in: {
      title: 'Entrar',
      desc: 'Seu email e senha.',
      button: 'Entrar',
    },
    sign_up: {
      title: 'Criar conta',
      desc: 'Ganhe 3 créditos grátis — sem cartão, sem trial absurdo.',
      button: 'Criar conta grátis',
    },
    reset: {
      title: 'Redefinir senha',
      desc: 'Mandamos um link pro seu email.',
      button: 'Enviar link',
    },
  };

  const t = titles[mode];

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      {/* Lado esquerdo — marca e copy */}
      <div className="hidden items-center justify-center bg-sidebar p-10 text-sidebar-foreground lg:flex">
        <div className="max-w-md space-y-6">
          <a href="/" className="flex items-center gap-3">
            <img src="/logo.svg" alt="Pórtico" className="size-10" />
            <span className="font-display text-2xl font-semibold">Pórtico</span>
          </a>
          <h1 className="font-display text-3xl leading-tight md:text-4xl">
            O portal entre o seu modelo
            <br />
            <span className="italic text-primary">e o retrato</span>.
          </h1>
          <p className="text-sidebar-foreground/80">
            Upload do seu SketchUp, Revit, planta ou sketch. Descreva a cena em
            português. Receba em 30 segundos um render fotorrealista com curadoria
            dos maiores escritórios do Brasil.
          </p>
          <ul className="space-y-2 text-sm text-sidebar-foreground/80">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 inline-block size-1 rounded-full bg-primary" />
              <span>Starter R$ 49/mês · 20 renders · PIX</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 inline-block size-1 rounded-full bg-primary" />
              <span>Pro R$ 149/mês · 80 renders 21:9 cinematic</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 inline-block size-1 rounded-full bg-primary" />
              <span>Agência R$ 499/mês · 350 renders + API</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 inline-block size-1 rounded-full bg-primary" />
              <span>NFS-e automática, cancele quando quiser</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Lado direito — form */}
      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="font-display text-2xl">{t.title}</CardTitle>
            <CardDescription>{t.desc}</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={onSubmit} noValidate>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@studio.com.br"
                />
              </div>
              {mode !== 'reset' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Senha</Label>
                    {mode === 'sign_in' && (
                      <button
                        type="button"
                        onClick={() => setMode('reset')}
                        className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                      >
                        Esqueci minha senha
                      </button>
                    )}
                  </div>
                  <Input
                    id="password"
                    type="password"
                    required
                    minLength={8}
                    autoComplete={mode === 'sign_in' ? 'current-password' : 'new-password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={mode === 'sign_up' ? 'Mínimo 8 caracteres' : '••••••••'}
                  />
                  {mode === 'sign_up' && (
                    <p className="text-xs text-muted-foreground">
                      Escolha algo forte — mínimo 8 caracteres.
                    </p>
                  )}
                </div>
              )}
              <Button type="submit" className="w-full" disabled={submitting} size="lg">
                {submitting ? 'Aguarde…' : t.button}
              </Button>
            </form>

            {mode === 'sign_in' && (
              <Button
                type="button"
                variant="link"
                className="mt-3 w-full"
                onClick={() => setMode('sign_up')}
              >
                Criar uma conta grátis (3 créditos)
              </Button>
            )}
            {mode === 'sign_up' && (
              <Button
                type="button"
                variant="link"
                className="mt-3 w-full"
                onClick={() => setMode('sign_in')}
              >
                Já tenho conta, entrar
              </Button>
            )}
            {mode === 'reset' && (
              <Button
                type="button"
                variant="link"
                className="mt-3 w-full"
                onClick={() => setMode('sign_in')}
              >
                Voltar pro login
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
